import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getSeoTracking } from './api';

const GA4_FULL_RE = /^G-[A-Z0-9]+$/;
const GA4_SUFFIX_RE = /^[A-Z0-9]{6,}$/;
const SCRIPT_ATTR = 'data-shipdesi-ga4';

/** Accept `G-XXXX` or suffix-only `XXXX` (not a numeric property ID). */
export function normalizeGa4MeasurementId(id) {
  if (typeof id !== 'string') return '';
  const s = id.trim().toUpperCase();
  if (!s) return '';
  if (GA4_FULL_RE.test(s)) return s;
  if (GA4_SUFFIX_RE.test(s) && /[A-Z]/.test(s)) return `G-${s}`;
  return '';
}

export function isValidGa4MeasurementId(id) {
  return Boolean(normalizeGa4MeasurementId(id));
}

/** Fields accepted by PUT /seo/tracking (`SeoTrackingUpdate`). GET also returns `updated_at`. */
export const SEO_TRACKING_KEYS = [
  'ga4_measurement_id',
  'ga4_enabled',
  'meta_pixel_id',
  'meta_pixel_enabled',
  'gtm_container_id',
  'gtm_enabled',
  'linkedin_partner_id',
  'linkedin_enabled',
  'google_site_verification',
  'bing_site_verification',
  'custom_head_scripts',
  'custom_body_start_scripts',
  'custom_body_end_scripts',
];

/** Drop `updated_at` and prefix a suffix-only GA4 ID. */
export function buildSeoTrackingPayload(tracking) {
  const src = tracking && typeof tracking === 'object' ? tracking : {};
  const payload = {};
  for (const k of SEO_TRACKING_KEYS) {
    if (src[k] !== undefined) payload[k] = src[k];
  }
  payload.ga4_measurement_id = normalizeGa4MeasurementId(payload.ga4_measurement_id) || '';
  return payload;
}

function ensureGtagStub() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

/** Inject gtag.js from Admin SEO (`seo_tracking`). Safe to call more than once. */
export function loadGa4(measurementId) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  const id = normalizeGa4MeasurementId(measurementId);
  if (!id) return false;
  if (document.querySelector(`script[${SCRIPT_ATTR}]`)) return true;

  ensureGtagStub();
  window.gtag('js', new Date());
  window.gtag('config', id, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  script.setAttribute(SCRIPT_ATTR, id);
  document.head.appendChild(script);
  return true;
}

export function sendPageView(pathnameSearch, title) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: pathnameSearch,
    page_title: title || (typeof document !== 'undefined' ? document.title : ''),
  });
}

/** Load gtag.js when Admin SEO has GA4 enabled with a G- measurement ID. */
export function useGa4Loader() {
  useEffect(() => {
    let cancelled = false;
    getSeoTracking()
      .then((r) => {
        if (cancelled) return;
        const tracking = r.data || {};
        if (!tracking.ga4_enabled) return;
        if (loadGa4(tracking.ga4_measurement_id)) {
          sendPageView(
            window.location.pathname + window.location.search,
            document.title,
          );
        }
      })
      .catch(() => { /* tracking is non-critical */ });
    return () => { cancelled = true; };
  }, []);
}

export function usePageView() {
  const location = useLocation();

  useEffect(() => {
    sendPageView(location.pathname + location.search, document.title);
  }, [location]);
}
